import axios from 'axios'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const PROPERTIES = 'api/properties'
const RENTALS    = 'api/rentals'
const ENQUIRIES  = 'api/enquiries'
const LANDSCAPING = 'api/landscaping'

const instance = axios.create({
    baseURL: BASE_URL
})

/**
 * Fetches a paginated, filtered list of properties.
 * Supported params: category, status, parish, property_type,
 *   price_min, price_max, area_min, area_max, featured,
 *   search, ordering, page
 * @param {object} params
 * @returns paginated response { count, next, previous, results[] }
 */
export async function getProperties(params) {
    try {
        const response = await instance.get(`/${PROPERTIES}/`, { params })
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Fetches a single property by its slug.
 * @param {string} slug  URL-safe unique identifier
 * @returns property detail object
 */
export async function getProperty(slug) {
    try {
        const response = await instance.get(`/${PROPERTIES}/${slug}/`)
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Fetches featured properties for the home page hero banner.
 * Returns the first page of featured properties.
 * @returns array of property objects
 */
export async function getBannerProperties() {
    try {
        const response = await instance.get(`/${PROPERTIES}/`, {
            params: { featured: true, page: 1 }
        })
        return response.data.results || []
    } catch (e) {
        throw e
    }
}

/**
 * Fetches featured properties (for-sale category).
 * The new backend has a single category field: 'property' or 'land'.
 * Returns the first page of featured properties.
 * @returns array of property objects
 */
export async function getFeaturedProperties() {
    try {
        const response = await instance.get(`/${PROPERTIES}/`, {
            params: { featured: true, category: 'property', page: 1 }
        })
        return response.data.results || []
    } catch (e) {
        throw e
    }
}

/**
 * Fetches featured land listings for the home page.
 * The new backend does not have a rent/buy split — this returns land listings.
 * @returns array of property objects
 */
export async function getFeaturedLand() {
    try {
        const response = await instance.get(`/${PROPERTIES}/`, {
            params: { featured: true, category: 'land', page: 1 }
        })
        return response.data.results || []
    } catch (e) {
        throw e
    }
}

/**
 * Fetches a paginated, filtered list of rentals.
 * Supported params: status, parish, property_type,
 *   price_min, price_max, area_min, area_max, featured,
 *   search, ordering, page
 * @param {object} params
 * @returns paginated response { count, next, previous, results[] }
 */
export async function getRentals(params) {
    try {
        const response = await instance.get(`/${RENTALS}/`, { params })
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Fetches a single rental by its slug.
 * @param {string} slug
 * @returns rental detail object
 */
export async function getRental(slug) {
    try {
        const response = await instance.get(`/${RENTALS}/${slug}/`)
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Submits a property enquiry.
 * @param {object} enquiry  { property (optional id), name, email, phone, message }
 * @returns { detail: string }
 */
export async function postEnquiry(enquiry) {
    try {
        const response = await instance.post(`/${ENQUIRIES}/`, enquiry)
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Submits a general contact / send-message enquiry (no property linked).
 * @param {string} email
 * @param {string} first_name
 * @param {string} last_name
 * @param {string} message
 * @returns { detail: string }
 */
export async function postContactForm(email, first_name, last_name, message) {
    try {
        const name = `${first_name} ${last_name}`.trim()
        const data = { name, email, message }
        const response = await instance.post(`/${ENQUIRIES}/`, data)
        return response.data
    } catch (e) {
        throw e
    }
}

/**
 * Submits a landscaping service request.
 * @param {object} request  { name, email, phone, service_type, parish, address, description }
 * @returns { detail: string }
 */
export async function postLandscapingRequest(request) {
    try {
        const response = await instance.post(`/${LANDSCAPING}/`, request)
        return response.data
    } catch (e) {
        throw e
    }
}
